/**
 * Shared entity "Activity" feed STATE — pure reducers + initial state, with NO
 * side-effect imports (no SuperFetch/store), so entity slices can import it
 * safely without creating a slice → store → slice import cycle. The fetch logic
 * lives separately in ./activitySaga (imported only by sagas).
 *
 * Each entity slice (Devices, Sites, Floors, Teams, AssetGroups, People) owns its
 * OWN `activity` sub-state so loading / success / error is real global state per
 * entity type, not component-local.
 *
 * Only ONE scope is held per slice at a time (the drawer currently showing its
 * Activity tab). `scopeKey` records which scope the held data belongs to so a
 * component can tell whether the store's data is its own, and stale responses for
 * a scope no longer displayed are ignored.
 */
export const activityInitialState = {
	status: 'idle', // 'idle' | 'loading' | 'success' | 'error'
	logs: [],
	total: 0,
	error: null,
	scopeKey: null,
	// The query that produced `logs`, kept so a REFRESH can re-issue the same fetch. Without it the
	// only record of the scope is `scopeKey`, which is a display key — re-deriving params by parsing it
	// would break the moment the key's shape changes.
	params: null
};

// Reducers to spread into a slice's `reducers` map. They read/write `state.activity`.
// Every entity slice therefore exposes `<slice>Actions.loadActivity` (+ Success/Error/clear).
export const activityReducers = {
	// Trigger — the saga performs the fetch. Marks loading and pins the scope
	// optimistically so the component does not re-dispatch for the same scope.
	loadActivity(state, action) {
		const scopeKey = action.payload?.scopeKey ?? null;
		// A refresh of the scope already held keeps its rows on screen while the fetch runs — an action
		// the operator just took should not blank the feed it is meant to update. A genuine scope change
		// still clears, because those rows belong to another entity.
		const sameScope = state.activity.scopeKey === scopeKey;
		state.activity = {
			status: 'loading',
			logs: sameScope ? state.activity.logs : [],
			total: sameScope ? state.activity.total : 0,
			error: null,
			scopeKey,
			params: action.payload?.params ?? null
		};
	},
	loadActivitySuccess(state, action) {
		const { scopeKey, logs, total } = action.payload;
		if (state.activity.scopeKey !== scopeKey) return; // stale — scope moved on
		state.activity.status = 'success';
		state.activity.logs = logs;
		state.activity.total = total;
		state.activity.error = null;
	},
	loadActivityError(state, action) {
		const { scopeKey, error } = action.payload;
		if (state.activity.scopeKey !== scopeKey) return; // stale — scope moved on
		state.activity.status = 'error';
		state.activity.error = error;
	},
	clearActivity(state) {
		state.activity = { ...activityInitialState };
	}
};
